import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router';
import { RELEASE } from '@crave/shared';
import { Button } from '@crave/ui';
import { api } from './api.ts';
import { authConfigured, onSession, signOut } from './lib/auth.ts';
import { useKitchenStaff } from './lib/staff.ts';
import { SendFeedback } from './components/SendFeedback.tsx';
import { Login } from './pages/Login.tsx';
import { Pos } from './pages/Pos.tsx';
import { clearPaymentAlerts, Queue } from './pages/Queue.tsx';
import { Tasting, type Batch } from './pages/Tasting.tsx';

const HEADER_CSS = `
/* The app fills the window: the header stays put and each screen scrolls inside it, so an iPad
   never scrolls the whole page under a tap. */
.kh-shell { display: flex; flex-direction: column; min-height: 100dvh; height: 100dvh; }
.kh-main { flex: 1; min-height: 0; overflow-y: auto; }
.kh { position: sticky; top: 0; z-index: 30; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 8px 16px; background: var(--ink); color: #fff; }
.kh-brand { display: inline-flex; align-items: center; gap: 10px; margin-right: auto; min-width: 0; }
.kh-brand img { height: 30px; width: auto; }
.kh-loc { font-size: 15px; font-weight: 600; color: rgba(255,255,255,.75); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kh-loc select { min-height: 40px; border-radius: 10px; border: 1px solid rgba(255,255,255,.25); background: rgba(255,255,255,.1);
  color: #fff; padding: 0 10px; font-weight: 600; }
.kh-loc option { color: #141414; }
.kh-release { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.55); font-variant-numeric: tabular-nums; }
.kh-tabs { display: inline-flex; padding: 3px; gap: 2px; border-radius: 12px; background: rgba(255,255,255,.12); }
.kh-tab { display: inline-flex; align-items: center; min-height: 40px; padding: 0 16px; border-radius: 9px; color: #fff;
  text-decoration: none; font-weight: 600; font-size: 16px; }
.kh-tab[aria-current='page'] { background: #fff; color: var(--ink); }
@media (max-width: 560px) { .kh-tabs { order: 3; width: 100%; } .kh-tab { flex: 1; justify-content: center; } }
`;

export function App() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  useEffect(() => onSession((s) => setSignedIn(Boolean(s))), []);
  if (authConfigured && signedIn === null) return null;
  if (!authConfigured || !signedIn) return <Login />;
  return <Shell />;
}

function Shell() {
  const staff = useKitchenStaff();
  // What this person's role opens: Kitchen gets the queue, Front counter the POS, managers both.
  const canQueue = staff.can('kitchen.view');
  // Where walk-ups are off, the POS tab hides for people who also have the queue; counter-only
  // staff still see the POS page, which says it is off.
  const canSell = staff.can('pos.use') && (staff.posEnabled || !canQueue);
  // The Tasting tab appears only while something is actually being tasted here, so it is never
  // one more thing to ignore on an ordinary Saturday.
  const mayLog = staff.can('tastings.log');
  const tastings = useQuery({
    queryKey: ['tastings', staff.slug],
    queryFn: async () =>
      (
        await api.get<{ batches: Batch[] }>(
          `/staff/tastings?location=${encodeURIComponent(staff.slug)}`,
        )
      ).data,
    enabled: mayLog && Boolean(staff.me.data),
    refetchInterval: 5 * 60_000,
  });
  const canTaste = mayLog && (tastings.data?.batches.length ?? 0) > 0;
  const signOutButton = (
    <Button
      variant="ghost"
      style={{ color: '#fff' }}
      onClick={() => {
        // Clear before and after: the queue stays mounted until the session change lands.
        clearPaymentAlerts();
        void signOut().finally(clearPaymentAlerts);
      }}
    >
      Sign out
    </Button>
  );
  if (!staff.me.data)
    return (
      <main style={{ maxWidth: 520, margin: '12vh auto', padding: 24, display: 'grid', gap: 16 }}>
        <p style={{ fontSize: 20, margin: 0 }}>
          {staff.me.isError
            ? staff.me.error instanceof Error
              ? staff.me.error.message
              : 'Could not load your access. Check the connection.'
            : 'Loading…'}
        </p>
        {staff.me.isError && (
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>
        )}
      </main>
    );
  return (
    <div className="kh-shell">
      <style href="crave-kitchen-header" precedence="default">
        {HEADER_CSS}
      </style>
      <header className="kh">
        <span className="kh-brand">
          <img src="/brand/logo-white-480.png" alt="Crave Pizza" />
          {staff.locations.length > 1 ? (
            <label className="kh-loc">
              <span className="visually-hidden" style={{ position: 'absolute', left: -9999 }}>
                Location
              </span>
              <select value={staff.slug} onChange={(e) => staff.choose(e.target.value)}>
                {staff.locations.map((l) => (
                  <option key={l.id} value={l.slug}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="kh-loc">{staff.locationName ?? 'Kitchen'}</span>
          )}
        </span>
        {(canTaste || (canSell && canQueue)) && (
          <nav className="kh-tabs" aria-label="Kitchen">
            {canQueue && (
              <NavLink to="/" end className="kh-tab">
                Queue
              </NavLink>
            )}
            {canSell && (
              <NavLink to="/pos" className="kh-tab">
                New order
              </NavLink>
            )}
            {canTaste && (
              <NavLink to="/tasting" className="kh-tab">
                Tasting
              </NavLink>
            )}
          </nav>
        )}
        <span className="kh-release" title="Crave release">
          {RELEASE}
        </span>
        <SendFeedback location={staff.slug} />
        {signOutButton}
      </header>
      <div className="kh-main">
        <Routes>
          {canTaste && <Route path="/tasting" element={<Tasting location={staff.slug} />} />}
          {canSell && (
            <Route
              path="/pos"
              element={
                <Pos location={staff.slug} staffName={staff.me.data?.display_name ?? null} />
              }
            />
          )}
          <Route
            path="*"
            element={
              canQueue ? (
                <Queue location={staff.slug} />
              ) : canSell ? (
                <Navigate to="/pos" replace />
              ) : (
                <main style={{ maxWidth: 560, margin: '10vh auto', padding: 24 }}>
                  <p style={{ fontSize: 20 }}>
                    Your role doesn’t include the kitchen queue or the POS. Ask the owner or a
                    manager to change it under Staff in admin.
                  </p>
                </main>
              )
            }
          />
        </Routes>
      </div>
    </div>
  );
}
